import { useState, useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  notification,
} from 'antd';
import {
  EditOutlined,
  MailOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import APIClient from '../api/api';
import { AlertType } from '../api/models';
import { Button, Column, DataTable } from '../components/ui';
import { PageHead, PageToolbar } from '../layout';

const AlertSettings = () => {
  const [alertTypes, setAlertTypes] = useState<AlertType[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState<AlertType | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadAlertTypes();
  }, []);

  async function loadAlertTypes() {
    setLoading(true);
    try {
      const resp = await APIClient.listAlertTypes();
      setAlertTypes(resp.alertTypes);
    } catch (e) {
      notification.error({ message: 'Failed to load alert types.' });
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleEnabled(alertType: AlertType) {
    try {
      await APIClient.updateAlertType(alertType.id, { enabled: !alertType.enabled });
      notification.success({
        message: `${alertType.name} ${!alertType.enabled ? 'enabled' : 'disabled'}.`,
      });
      loadAlertTypes();
    } catch (e) {
      notification.error({ message: 'Failed to update alert type.' });
    }
  }

  async function handleToggleEmail(alertType: AlertType) {
    try {
      await APIClient.updateAlertType(alertType.id, {
        emailEnabled: !alertType.emailEnabled,
      });
      notification.success({
        message: `Email notifications ${!alertType.emailEnabled ? 'enabled' : 'disabled'} for ${
          alertType.name
        }.`,
      });
      loadAlertTypes();
    } catch (e) {
      notification.error({ message: 'Failed to update alert type.' });
    }
  }

  function openEdit(alertType: AlertType) {
    setEditModal(alertType);
    const config = alertType.config || {};
    const overridesMap = config.providerOverrides || {};
    const overridesList = Object.entries(overridesMap).map(([provider, hours]) => ({
      provider,
      hours,
    }));
    form.setFieldsValue({
      emailRecipients: alertType.emailRecipients,
      ...config,
      providerOverrides: overridesList,
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const values = await form.validateFields();
      if (!editModal) return;

      const { emailRecipients, providerOverrides, ...configValues } = values;

      if (Array.isArray(providerOverrides) && providerOverrides.length > 0) {
        const overridesMap: Record<string, number> = {};
        for (const item of providerOverrides) {
          if (item?.provider && item?.hours != null) {
            overridesMap[item.provider] = item.hours;
          }
        }
        if (Object.keys(overridesMap).length > 0) {
          configValues.providerOverrides = overridesMap;
        }
      }

      await APIClient.updateAlertType(editModal.id, {
        emailRecipients,
        config: configValues,
      });

      notification.success({ message: `${editModal.name} updated.` });
      setEditModal(null);
      form.resetFields();
      loadAlertTypes();
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error || 'Failed to save settings.';
      notification.error({ message: errorMsg });
    } finally {
      setSaving(false);
    }
  }

  function renderProviderOverrides(placeholder: string) {
    return (
      <Form.Item label="Per-Provider Threshold Overrides">
        <Form.List name="providerOverrides">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  key={key}
                  style={{ display: 'flex', marginBottom: 8 }}
                  align="baseline"
                >
                  <Form.Item
                    {...restField}
                    name={[name, 'provider']}
                    rules={[{ required: true, message: 'Provider required' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder={`e.g. ${placeholder}`} style={{ width: 200 }} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'hours']}
                    rules={[{ required: true, message: 'Hours required' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber min={1} placeholder="Hours" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => add()}
                iconLeft={<PlusOutlined />}
              >
                Add Override
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>
    );
  }

  function renderConfigFields(alertType: AlertType | null) {
    if (!alertType) return null;

    switch (alertType.slug) {
      case 'domain_not_renewed_rejected':
      case 'domain_not_renewed_acknowledged':
        return (
          <>
            <p className="text-text-3 text-[12px] mb-4">
              NC pick emails are sent 30 days post-expiration. Spaceship at 40 days. Picks remain
              valid for 45 days after the send date (full window: 75-85 days).
            </p>
            <Form.Item
              label="Days After Expiration to Alert"
              name="daysAfterExpiration"
              rules={[{ required: true }]}
              tooltip="Must be after pick emails are sent (day 30 for NC). Default: 45"
            >
              <InputNumber min={1} max={365} />
            </Form.Item>
            <Form.Item
              label="Check Window (Days)"
              name="checkWindowDays"
              rules={[{ required: true }]}
              tooltip="How far back to look. Should cover the full pick validity window. Default: 75"
            >
              <InputNumber min={1} max={365} />
            </Form.Item>
          </>
        );
      case 'pick_volume_anomaly':
        return (
          <>
            <Form.Item
              label="Percent Change Threshold"
              name="percentChangeThreshold"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={1000} addonAfter="%" />
            </Form.Item>
            <Form.Item
              label="Minimum Baseline Count"
              name="minimumBaselineCount"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item
              label="Comparison Period (Days)"
              name="comparisonPeriodDays"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={365} />
            </Form.Item>
          </>
        );
      case 'support_no_response':
        return (
          <Form.Item
            label="Days Without Response"
            name="daysWithoutResponse"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={30} />
          </Form.Item>
        );
      case 'nc_api_token_invalid':
        return (
          <Form.Item
            label="Recheck Interval (Hours)"
            name="recheckIntervalHours"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} />
          </Form.Item>
        );
      case 'parking_data_stale':
        return (
          <>
            <Form.Item
              label="Default Stale Threshold (Hours)"
              name="staleThresholdHours"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item label="Providers" name="providers">
              <Select
                mode="tags"
                placeholder="Leave empty to monitor all (e.g. sedo-nc-customer)"
              />
            </Form.Item>
            {renderProviderOverrides('sedo-nc-customer')}
          </>
        );
      case 'payout_data_stale':
        return (
          <>
            <Form.Item
              label="Default No Payouts Threshold (Hours)"
              name="staleThresholdHours"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item label="Venues" name="providers">
              <Select mode="tags" placeholder="Leave empty to monitor all (e.g. sedo:account)" />
            </Form.Item>
            {renderProviderOverrides('sedo:parity1')}
          </>
        );
      case 'sales_data_stale':
        return (
          <>
            <Form.Item
              label="Default No Sales Threshold (Hours)"
              name="staleThresholdHours"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item label="Venues" name="providers">
              <Select mode="tags" placeholder="Leave empty to monitor all (e.g. sedo:account)" />
            </Form.Item>
            {renderProviderOverrides('sedo:parity1')}
          </>
        );
      default:
        return null;
    }
  }

  const columns: Column<AlertType>[] = [
    {
      key: 'name',
      title: 'Name',
      width: 260,
      render: (a) => <span className="text-text font-medium text-[12.5px]">{a.name}</span>,
    },
    {
      key: 'description',
      title: 'Description',
      render: (a) => <span className="text-text-3 text-[12px]">{a.description}</span>,
    },
    {
      key: 'enabled',
      title: 'Enabled',
      width: 90,
      render: (a) => (
        <Switch checked={a.enabled} onChange={() => handleToggleEnabled(a)} size="small" />
      ),
    },
    {
      key: 'emailEnabled',
      title: 'Email',
      width: 90,
      render: (a) => (
        <Switch
          checked={a.emailEnabled}
          onChange={() => handleToggleEmail(a)}
          size="small"
          checkedChildren={<MailOutlined />}
        />
      ),
    },
    {
      key: 'emailRecipients',
      title: 'Recipients',
      width: 220,
      render: (a) =>
        a.emailRecipients ? (
          <span className="text-text-2 text-[12px] font-mono truncate block max-w-[220px]">
            {a.emailRecipients}
          </span>
        ) : (
          <span className="text-text-3">—</span>
        ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      width: 140,
      render: (a) => (
        <Button size="sm" variant="ghost" iconLeft={<EditOutlined />} onClick={() => openEdit(a)}>
          Configure
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Alert Settings"
        subtitle="Per-alert thresholds, enablement, and notification recipients."
      />
      <PageToolbar>
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {alertTypes.length > 0 ? (
            <>
              total <b className="text-text-2">{alertTypes.length}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={alertTypes}
        rowKey={(a) => String(a.id)}
        loading={loading}
        emptyMessage="No alert types."
      />

      <Modal
        title={editModal ? `Configure: ${editModal.name}` : 'Configure Alert Type'}
        open={!!editModal}
        onCancel={() => {
          setEditModal(null);
          form.resetFields();
        }}
        onOk={handleSave}
        confirmLoading={saving}
        width={520}
      >
        {editModal && (
          <Form form={form} layout="vertical">
            <Form.Item
              label="Email Recipients (comma-separated)"
              name="emailRecipients"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') return Promise.resolve();
                    const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
                    const invalid = value
                      .split(',')
                      .map((e: string) => e.trim())
                      .filter((e: string) => e !== '' && !emailRegex.test(e));
                    if (invalid.length > 0) {
                      return Promise.reject(
                        `Invalid email address${invalid.length > 1 ? 'es' : ''}: ${invalid.join(
                          ', ',
                        )}`,
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="user@example.com, other@example.com" />
            </Form.Item>
            {renderConfigFields(editModal)}
          </Form>
        )}
      </Modal>
    </>
  );
};

export default AlertSettings;
